
import { APP_VERSION } from '../constants';

/**
 * Performs a hard reset of the application, clearing caches, service workers,
 * and localStorage, while trying to preserve the authentication session.
 */
export const performHardReset = async (redirectPath?: string, options: { showOverlay?: boolean, isManual?: boolean } = {}) => {
    const { showOverlay = false, isManual = false } = options;
    
    try {
        console.warn(`[UpdateUtils] Starting hard reset (overlay: ${showOverlay}, manual: ${isManual})...`);

        if (showOverlay) {
            // --- Immediate Visual Mask ---
            window.dispatchEvent(new CustomEvent('show-global-loading', { 
                detail: { 
                    messageKey: 'settings.updating_app', 
                    fallback: 'Baixando atualização...' 
                } 
            }));
        }
        
        // 1. Identify and save the current session and essential states
        const authKey = Object.keys(localStorage).find(key => key.includes('sb-auth-token') || key.includes('auth-token'));
        const session = authKey ? localStorage.getItem(authKey) : null;
        const userProfile = localStorage.getItem('libritui_user');
        const theme = localStorage.getItem('themeMode'); 
        const pdfThemeMode = localStorage.getItem('pdfThemeMode'); 
        const accentColor = localStorage.getItem('libritui_themeAccentColor');
        const targetLanguage = localStorage.getItem('targetLanguage');
        const i18nextLng = localStorage.getItem('i18nextLng');
        const currentVersion = APP_VERSION;
        
        // Save interactions cache, comment caches, and flashcard store caches
        const allKeys = Object.keys(localStorage);
        const interactionCaches: Record<string, string> = {};
        allKeys.forEach(key => {
            if (
                key === 'libritui_interactions_cache' || 
                key.startsWith('comments_cache_') || 
                key.startsWith('cache_') ||
                key.startsWith('fc_store_deck_') ||
                key.startsWith('libritui_cache_') ||
                key.startsWith('libritui_favorite_')
            ) {
                interactionCaches[key] = localStorage.getItem(key) || '';
            }
        });

        // 2. Clear all browser caches
        if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(
                cacheNames
                    .filter(name => name !== 'whisper-stt-cache' && name !== 'nllb-onnx-models-v1' && name !== 'free-dict-cache' && name !== 'rich-dict-cache-v2' && name !== 'bergamot-models-v1' && name !== 'marian-onnx-models-v1' && name !== 'transformers-cache' && name !== 'piper-voices-cache')
                    .map(name => caches.delete(name))
            );
        }

        // 3. Remove Service Workers
        if ('serviceWorker' in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map(reg => reg.unregister()));
        }

        // 4. Clear LocalStorage and restore essentials
        localStorage.clear();
        localStorage.setItem('last_accepted_version', currentVersion);
        localStorage.setItem('app_version', currentVersion);
        
        if (session && authKey) localStorage.setItem(authKey, session);
        if (userProfile) localStorage.setItem('libritui_user', userProfile);
        if (theme) localStorage.setItem('themeMode', theme);
        if (pdfThemeMode) localStorage.setItem('pdfThemeMode', pdfThemeMode);
        if (accentColor) localStorage.setItem('libritui_themeAccentColor', accentColor);
        if (targetLanguage) localStorage.setItem('targetLanguage', targetLanguage);
        if (i18nextLng) localStorage.setItem('i18nextLng', i18nextLng);

        // Restore caches
        Object.entries(interactionCaches).forEach(([key, value]) => {
            localStorage.setItem(key, value);
        });

        // --- Persistence Flags ---
        localStorage.setItem('libritui_updating', 'true');
        sessionStorage.removeItem('libritui_drive_synced');
        sessionStorage.removeItem('libritui_last_reload_time');
        if (isManual) {
            localStorage.setItem('libritui_update_success_pending', 'true');
        }

        console.log('[UpdateUtils] Reset complete. Reloading...');

        // 5. Force a hard reload from the server or redirect to target
        setTimeout(() => {
            const cacheBuster = `t=${Date.now()}`;
            const targetBase = redirectPath || window.location.pathname || '/';
            const cleanPath = targetBase.split('?')[0];
            const target = `${cleanPath}?${cacheBuster}`;
            window.location.replace(target);
        }, showOverlay ? 600 : 100);
    } catch (err) {
        console.error("[UpdateUtils] Error processing update:", err);
        // Fallback
        localStorage.setItem('libritui_updating', 'true');
        if (isManual) localStorage.setItem('libritui_update_success_pending', 'true');
        
        const cacheBuster = `t=${Date.now()}`;
        const targetBase = redirectPath || window.location.pathname || '/';
        const cleanPath = targetBase.split('?')[0];
        window.location.href = `${cleanPath}?${cacheBuster}`;
    }
};

/**
 * Checks if an update is available and performs it if needed.
 * Includes a 20-minute cooldown to prevent excess checks.
 */
export const checkAndPerformUpdate = async (targetPath?: string) => {
    try {
        const lastCheck = parseInt(localStorage.getItem('libritui_last_update_check') || '0');
        const now = Date.now();
        
        // Cooldown: 20 minutes
        if (now - lastCheck < 20 * 60 * 1000) return false;

        // Update check timestamp
        localStorage.setItem('libritui_last_update_check', now.toString());

        // Light fetch for version info
        const response = await fetch(`/version.json?v=${Date.now()}`, { 
            cache: 'no-store',
            headers: {
                'Pragma': 'no-cache',
                'Cache-Control': 'no-cache',
                'Accept': 'application/json'
            }
        });
        if (!response.ok) return false;

        const data = await response.json();
        const serverVersion = data.version;
        const localVersion = APP_VERSION;

        if (serverVersion && serverVersion !== localVersion) {
            console.log(`[Update] New version detected: ${serverVersion}. Local: ${localVersion}`);
            // Background update is instant, no overlay
            await performHardReset(targetPath, { showOverlay: false, isManual: false });
            return true;
        }
    } catch (e) {
        console.warn("[Update] Silent version check failed:", e);
    }
    return false;
};
